import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { movies, regionOptions, yearOptions } from '@/mocks/movies';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import MovieCard from '@/components/feature/MovieCard';
import SearchBar from '@/components/feature/SearchBar';
import FilterGroup from '@/components/feature/FilterGroup';
import Pagination from '@/components/feature/Pagination';

const PAGE_SIZE = 8;

const typeOptions = [
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const keyword = (searchParams.get('q') ?? '').trim();
  const type = searchParams.get('type') ?? '';
  const region = searchParams.get('region') ?? '';
  const year = searchParams.get('year') ?? '';
  const page = Number(searchParams.get('page') ?? '1') || 1;

  useDocumentMeta({
    title: keyword ? `“${keyword}” 搜索结果 - 神马影院` : '搜索影视 - 神马影院',
    description: keyword
      ? `在神马影院搜索“${keyword}”的相关电影、电视剧资源，共为你匹配到相关影视内容，可按类型、地区与年份进一步筛选。`
      : '神马影院全站搜索，输入关键词即可查找电影、电视剧、演员与类型等影视资源。',
    keywords: keyword ? `${keyword},神马影院搜索,影视搜索` : '神马影院,影视搜索,电影搜索,电视剧搜索',
    path: '/search',
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (keyword) next.set('q', keyword);
    setSearchParams(next, { replace: true });
  };

  const results = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.director,
        movie.region,
        movie.synopsis,
        ...movie.actors,
        ...movie.genres,
        movie.type === 'movie' ? '电影' : '电视剧',
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(lower)) return false;
      if (type && movie.type !== type) return false;
      if (region && movie.region !== region) return false;
      if (year && String(movie.year) !== year) return false;
      return true;
    });
  }, [keyword, type, region, year]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const recommendations = useMemo(() => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 4), []);
  const activeFilterCount = [type, region, year].filter(Boolean).length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-700">搜索</span>
      </nav>

      <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {keyword ? (
            <span className="flex flex-wrap items-center gap-2">
              搜索“<span className="text-accent-600">{keyword}</span>”
            </span>
          ) : (
            '全站影视搜索'
          )}
        </h1>
        <div className="mt-4 max-w-2xl">
          <SearchBar variant="page" initialValue={keyword} />
        </div>
        {keyword && (
          <p className="mt-4 flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-file-list-3-line text-accent-500"></i>
            共为你找到 <strong className="text-foreground-900">{results.length}</strong> 部相关影视
          </p>
        )}
      </div>

      {keyword ? (
        <div className="mt-6">
          <div className={`${filterOpen ? 'block' : 'hidden'} mb-5 rounded-xl border border-background-200 bg-background-50 p-4 md:block`}>
            <FilterGroup
              label="类型"
              options={typeOptions.map((item) => item.label)}
              value={type ? (type === 'movie' ? '电影' : '电视剧') : ''}
              onChange={(value) => updateParam('type', value === '电影' ? 'movie' : value === '电视剧' ? 'tv' : '')}
            />
            <FilterGroup label="地区" options={regionOptions} value={region} onChange={(value) => updateParam('region', value)} />
            <FilterGroup label="年份" options={yearOptions} value={year} onChange={(value) => updateParam('year', value)} />
            <div className="flex items-center justify-between gap-3 pt-3">
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-refresh-line"></i> 重置筛选
                </button>
              ) : <span />}
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 md:hidden"
              >
                <i className="ri-filter-3-line"></i> 筛选{activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
              </button>
            </div>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                {visible.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showType />
                ))}
              </div>
              <Pagination
                current={safePage}
                totalPages={totalPages}
                onChange={(nextPage) => updateParam('page', String(nextPage))}
              />
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">没有找到与“{keyword}”相关的影视</h3>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者先看看下面这些高分推荐。</p>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
                >
                  <i className="ri-refresh-line"></i> 清除筛选条件
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6">
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">可以搜索作品名、演员、导演，也可以直接输入类型，如“科幻”“悬疑”。</p>
          </div>
        </div>
      )}

      <section className="mt-10">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
          <i className="ri-thumb-up-line text-accent-500"></i> 高分影视推荐
        </h2>
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {recommendations.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>
    </div>
  );
}